/**
 * App Container
 * 
 * This is the entry point container that connects the App component
 * to Redux state and actions
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { Container, Row, Col, Alert, Spinner, Button, Card, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { bomAPI } from '../../services/api';

import FileUpload from '../../components/FileUpload';
import BOMDataDisplay from '../../components/BOMDataDisplay';
import OnboardingMessage from '../../components/OnboardingMessage';

import {
  initializeSession,
  logoutSessionRequest,
  loadBOMData,
  updateBOMData,
  submitBOMData,
  clearUploadedData,
  clearError,
  clearSuccess,
} from './actions';

import {
  makeSelectSession,
  makeSelectSessionLoading,
  makeSelectSessionError,
  makeSelectBOMData,
  makeSelectCSVData,
  makeSelectLoading,
  makeSelectSubmitting,
  makeSelectError,
  makeSelectSuccess,
} from './selectors';

export function App({
  session,
  sessionLoading,
  sessionError,
  bomData,
  csvData,
  loading,
  submitting,
  error,
  success,
  onInitializeSession,
  onLogoutSession,
  onLoadBOMData,
  onUpdateBOMData,
  onSubmitBOMData,
  onClearUploadedData,
  onClearError,
  onClearSuccess,
}) {
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [createBOMDialogTrigger, setCreateBOMDialogTrigger] = useState(0);
  const [fileUploadResetTrigger, setFileUploadResetTrigger] = useState(0);
  const [uploadedCSVFiles, setUploadedCSVFiles] = useState([]);
  const [isCsvValidationPending, setIsCsvValidationPending] = useState(false);
  const [factoryBOMRows, setFactoryBOMRows] = useState([]);
  const [factoryBOMLoading, setFactoryBOMLoading] = useState(false);
  const [factoryBOMError, setFactoryBOMError] = useState('');
  const [sort12NCOrder, setSort12NCOrder] = useState('none');
  const [sortBatchIdOrder, setSortBatchIdOrder] = useState('none');
  const [factoryCodeFilter, setFactoryCodeFilter] = useState('all');
  const [uploadedFilter, setUploadedFilter] = useState('all');
  const [factoryBOMSearchTerm, setFactoryBOMSearchTerm] = useState('');
  const [factoryTablePage, setFactoryTablePage] = useState(1);
  const [viewBOMRow, setViewBOMRow] = useState(null);
  const [preselectedCreateBOM12NC, setPreselectedCreateBOM12NC] = useState('');
  const [preselectedCreateBOMFactoryCode, setPreselectedCreateBOMFactoryCode] = useState('');
  const FACTORY_TABLE_PAGE_SIZE = 10;

  const cycleSortOrder = (currentOrder) => {
    if (currentOrder === 'none') return 'asc'; 
    if (currentOrder === 'asc') return 'desc';
    return 'none';
  };

  const getSortIcon = (order) => {
    if (order === 'asc') return '↑';
    if (order === 'desc') return '↓';
    return '↕';
  };

  // Auto-dismiss success toast after 3 seconds
  useEffect(() => {
    if (success) {
      const isPreviewExtractionMessage = String(success).startsWith('Successfully extracted ');

      if (isPreviewExtractionMessage) {
        setShowSuccessToast(false);
        onClearSuccess();
        return;
      }

      setShowSuccessToast(true);
      const timer = setTimeout(() => {
        setShowSuccessToast(false);
        onClearSuccess();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success, onClearSuccess]);

  useEffect(() => {
    if (!error && !factoryBOMError) {
      return undefined;
    }

    const handleUserActionDismiss = () => {
      if (error) {
        onClearError();
      }
      if (factoryBOMError) {
        setFactoryBOMError('');
      }
    };

    window.addEventListener('pointerdown', handleUserActionDismiss, { once: true });
    window.addEventListener('keydown', handleUserActionDismiss, { once: true });

    return () => {
      window.removeEventListener('pointerdown', handleUserActionDismiss);
      window.removeEventListener('keydown', handleUserActionDismiss);
    };
  }, [error, factoryBOMError, onClearError]);

  // Reject selected CSV files when CSV parsing fails.
  useEffect(() => {
    if (!isCsvValidationPending || loading) {
      return;
    }

    if (typeof error === 'string' && error.trim()) {
      setUploadedCSVFiles([]);
      setFileUploadResetTrigger((currentValue) => currentValue + 1);
      setIsCsvValidationPending(false);
      return;
    }

    if (Array.isArray(csvData?.originalCsvData)) {
      setIsCsvValidationPending(false);
    }
  }, [isCsvValidationPending, loading, error, csvData]);

  // Initialize session on mount - only once
  // SKIP if we're on /homepage (already authenticated - server protected this route)
  useEffect(() => {
    const currentPath = window.location.pathname;
   onInitializeSession();
  }, []); // Empty dependency array ensures it runs only once

  const handleFileSelect = (files) => {
    const normalizedFiles = Array.isArray(files) ? files : [];
    setUploadedCSVFiles(normalizedFiles);
    setIsCsvValidationPending(normalizedFiles.length > 0);
    onLoadBOMData(normalizedFiles);
  };

  const handleCSVUpload = async (files = []) => {
    const normalizedFiles = Array.isArray(files) && files.length > 0
      ? files
      : uploadedCSVFiles;

    if (!Array.isArray(normalizedFiles) || normalizedFiles.length === 0) {
      throw new Error('No CSV files selected for upload.');
    }

    const uploadResponse = await bomAPI.uploadCSVFiles(normalizedFiles);
    await loadFactoryBOMData();
    return uploadResponse;
  };

  const handleDataUpdate = (updatedData) => {
    onUpdateBOMData(updatedData);
  };

  const handleSubmit = (dataToSubmit) => {
    onSubmitBOMData(dataToSubmit);
  };

  const handleUploadSuccess = async (message) => {
    // Always refetch so the table reflects the latest JSONs from the server.
    await loadFactoryBOMData();
    setFileUploadResetTrigger((currentValue) => currentValue + 1);
    setUploadedCSVFiles([]);
    onClearUploadedData(message);
  };

  const extractFactoryCodes = (supportedFactories) => {
    const discoveredCodes = [];

    const collectCodes = (value) => {
      if (value === null || value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(collectCodes);
        return;
      }

      if (typeof value === 'string') {
        value
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
          .forEach((entry) => discoveredCodes.push(entry));
        return;
      }

      if (typeof value === 'number') {
        discoveredCodes.push(String(value));
        return;
      }

      if (typeof value !== 'object') {
        return;
      }

      // Common /details response wrapper shapes.
      if (Object.prototype.hasOwnProperty.call(value, 'supportedFactories')) {
        collectCodes(value.supportedFactories);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'data')) {
        collectCodes(value.data);
      }

      // Common factory-code item shapes.
      const directCode = value.factoryCode
        || value.factorycode
        || value.code
        || value.value
        || value.id;
      if (directCode !== undefined && directCode !== null) {
        collectCodes(directCode);
      }
    };

    collectCodes(supportedFactories);

    const normalizedCodes = discoveredCodes
      .map((code) => String(code || '').trim())
      .filter((code) => code.length > 0 && code !== '[object Object]');

    return Array.from(new Set(normalizedCodes));
  };

  const normalizeFactoryRows = (payload, factoryCode) => {
    const sourceRows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

    return sourceRows.map((row) => {
      const twelveNC = String(
        row?.twelveNC || row?.twelvenc || row?.id || ''
      ).trim();

      return {
        twelveNC,
        batchId: row?.batchId || row?.batchid || row?.batch || '-',
        factoryCode: row?.factoryCode || row?.factorycode || factoryCode,
        effectiveDate: row?.effectiveDate || row?.effectivedate || '-',
        data: row?.data ?? null,
      };
    });
  };

  const loadFactoryBOMData = useCallback(async () => {
    const supportedFactories = session?.userDetails?.supportedFactories
      ?? session?.user?.supportedFactories
      ?? session?.supportedFactories
      ?? session?.userDetails
      ?? session?.user;
    const factoryCodes = extractFactoryCodes(supportedFactories);

    if (factoryCodes.length === 0) {
      setFactoryBOMRows([]);
      setFactoryBOMError('');
      return;
    }

    setFactoryBOMLoading(true);
    setFactoryBOMError('');

    try {
      const response = await bomAPI.getBOMLinks();
      setFactoryBOMRows(normalizeFactoryRows(response));
    } catch (error) {
      setFactoryBOMRows([]);
      setFactoryBOMError(error.message || 'Failed to load factory BOM details');
    } finally {
      setFactoryBOMLoading(false);
    }
  }, [session]);

  useEffect(() => {
    loadFactoryBOMData();
  }, [loadFactoryBOMData]);

  const factoryCodeOptions = useMemo(() => {
    const uniqueCodes = Array.from(new Set(factoryBOMRows.map((row) => String(row.factoryCode || '').trim()).filter(Boolean)));
    return uniqueCodes.sort((a, b) => a.localeCompare(b));
  }, [factoryBOMRows]);

  const cycleFactoryCodeFilter = () => {
    if (factoryCodeOptions.length === 0) {
      setFactoryCodeFilter('all');
      return;
    }

    const allOptions = ['all', ...factoryCodeOptions];
    const currentIndex = allOptions.indexOf(factoryCodeFilter);
    const nextIndex = currentIndex < 0 || currentIndex === allOptions.length - 1
      ? 0
      : currentIndex + 1;

    setFactoryCodeFilter(allOptions[nextIndex]);
  };

  const cycleUploadedFilter = () => {
    const allOptions = ['all', 'uploaded', 'not_uploaded'];
    const currentIndex = allOptions.indexOf(uploadedFilter);
    const nextIndex = currentIndex < 0 || currentIndex === allOptions.length - 1
      ? 0
      : currentIndex + 1;

    setUploadedFilter(allOptions[nextIndex]);
  };

  const getUploadedFilterIcon = () => {
    if (uploadedFilter === 'uploaded') return '✓';
    if (uploadedFilter === 'not_uploaded') return '✗';
    return '◉';
  };

  const formatBOMPayload = (payload) => {
    if (payload === null || payload === undefined) {
      return '';
    }

    if (typeof payload === 'string') {
      try {
        return JSON.stringify(JSON.parse(payload), null, 2);
      } catch {
        return payload;
      }
    }

    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  };

  const filteredAndSortedFactoryRows = useMemo(() => {
    const searchTerm = factoryBOMSearchTerm.trim().toLowerCase();

    const filteredRows = factoryBOMRows.filter((row) => {
      const rowFactoryCode = String(row.factoryCode || '').trim();
      const hasBOMData = row.data !== null && row.data !== undefined;
      const twelveNC = String(row.twelveNC || '').toLowerCase();
      const effectiveDate = String(row.effectiveDate || '').toLowerCase();

      if (factoryCodeFilter !== 'all' && rowFactoryCode !== factoryCodeFilter) {
        return false;
      }

      if (uploadedFilter === 'uploaded' && !hasBOMData) {
        return false;
      }

      if (uploadedFilter === 'not_uploaded' && hasBOMData) {
        return false;
      }

      if (searchTerm && !twelveNC.includes(searchTerm) && !effectiveDate.includes(searchTerm)) {
        return false;
      }

      return true;
    });

    const sortedRows = [...filteredRows].sort((leftRow, rightRow) => {
      if (sort12NCOrder !== 'none') {
        const left12NC = String(leftRow.twelveNC || '');
        const right12NC = String(rightRow.twelveNC || '');
        const result = left12NC.localeCompare(right12NC, undefined, { numeric: true, sensitivity: 'base' });
        if (result !== 0) {
          return sort12NCOrder === 'asc' ? result : -result;
        }
      }

      if (sortBatchIdOrder !== 'none') {
        const leftBatchId = String(leftRow.batchId || '');
        const rightBatchId = String(rightRow.batchId || '');
        const result = leftBatchId.localeCompare(rightBatchId, undefined, { numeric: true, sensitivity: 'base' });
        if (result !== 0) {
          return sortBatchIdOrder === 'asc' ? result : -result;
        }
      }

      return 0;
    });

    return sortedRows;
  }, [factoryBOMRows, factoryCodeFilter, uploadedFilter, factoryBOMSearchTerm, sort12NCOrder, sortBatchIdOrder]);

  const totalFactoryPages = Math.max(1, Math.ceil(filteredAndSortedFactoryRows.length / FACTORY_TABLE_PAGE_SIZE));

  useEffect(() => {
    setFactoryTablePage(1);
  }, [factoryCodeFilter, uploadedFilter, factoryBOMSearchTerm, sort12NCOrder, sortBatchIdOrder]);

  useEffect(() => {
    if (factoryTablePage > totalFactoryPages) {
      setFactoryTablePage(totalFactoryPages);
    }
  }, [factoryTablePage, totalFactoryPages]);

  const paginatedFactoryRows = useMemo(() => {
    const startIndex = (factoryTablePage - 1) * FACTORY_TABLE_PAGE_SIZE;
    return filteredAndSortedFactoryRows.slice(startIndex, startIndex + FACTORY_TABLE_PAGE_SIZE);
  }, [filteredAndSortedFactoryRows, factoryTablePage]);

  const supportedFactoryCodes = useMemo(
    () => extractFactoryCodes(
      session?.userDetails?.supportedFactories
      ?? session?.user?.supportedFactories
      ?? session?.supportedFactories
      ?? session?.userDetails
      ?? session?.user
    ),
    [session?.userDetails?.supportedFactories, session?.user?.supportedFactories, session?.supportedFactories, session?.userDetails, session?.user]
  );

  // When on protected homepage routes, render app immediately without waiting for session.
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
  const isOnHomepage = currentPath === '/homepage' || currentPath === '/api/homepage';

  // Don't show main app if no session AND not on homepage - redirect will happen in saga
  if (!session && !isOnHomepage) {
    return null; // Return nothing - redirect happening in background
  }

  // Check for third-party-writer permission in userDetails (from session or userDetails)
  const userRoles = (session?.userDetails?.roles) || (session?.user?.roles) || {};
  const hasThirdPartyWriter = !!userRoles['third-party-writer'];

  // If user does not have permission, show onboarding message
  if (!hasThirdPartyWriter) {
    return <OnboardingMessage />;
  }

  // Main app - shown when session exists OR when on /homepage (already authenticated)
  return (
    <div className="App">
      <div className="floating-toast-stack" aria-live="polite" aria-atomic="true">
        {error && (
          <div className="floating-toast floating-toast-error" role="alert" aria-live="assertive">
            <div className="floating-toast-content">
              <strong>Error</strong>
              <p className="mb-0">{error}</p>
            </div>
            <Button
              variant="link"
              className="floating-toast-close"
              onClick={onClearError}
              aria-label="Dismiss error"
            >
              ×
            </Button>
          </div>
        )}

        {!factoryBOMLoading && factoryBOMError && (
          <div className="floating-toast floating-toast-warning" role="alert" aria-live="assertive">
            <div className="floating-toast-content">
              <strong>Warning</strong>
              <p className="mb-0">{factoryBOMError}</p>
            </div>
            <Button
              variant="link"
              className="floating-toast-close"
              onClick={() => setFactoryBOMError('')}
              aria-label="Dismiss warning"
            >
              ×
            </Button>
          </div>
        )}

        {success && showSuccessToast && (
          <div className="floating-toast floating-toast-success">
            <div className="floating-toast-content">
              <p className="mb-0">{success}</p>
            </div>
          </div>
        )}
      </div>

      {/* Header with Logout */}
      <header className="app-header py-3 mb-4">
        <Container fluid>
          <Row className="align-items-center g-3">
            <Col>
              <div className="app-brand-block">
                <h1 className="h4 mb-0">Factory Data Management Tool</h1>
              </div>
            </Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              {/* Logged In User Card */}
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
                padding: '6px 14px',
                textAlign: 'center',
                minWidth: '120px',
              }}>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Logged In As</div>
                <div style={{ fontSize: '0.78rem', color: '#ffffff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                  {session?.userDetails?.email || session?.user?.email || session?.userDetails?.name || session?.user?.name || session?.userDetails?.username || session?.user?.username || 'Unknown'}
                </div>
              </div>
              {/* Visible BOMs Card */}
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
                padding: '6px 14px',
                textAlign: 'center',
                minWidth: '90px',
              }}>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Visible BOMs</div>
                <div style={{ fontSize: '1.1rem', color: '#00e487', fontWeight: 700, lineHeight: 1 }}>
                  {factoryBOMRows.filter(row => row.data !== null && row.data !== undefined).length}
                </div>
              </div>
              {/* Factory 12NCs Card */}
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
                padding: '6px 14px',
                textAlign: 'center',
                minWidth: '90px',
              }}>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Factory 12NCs</div>
                <div style={{ fontSize: '1.1rem', color: '#00e487', fontWeight: 700, lineHeight: 1 }}>
                  {new Set(factoryBOMRows.map(row => row.twelveNC).filter(Boolean)).size}
                </div>
              </div>
            </Col>
            {(session ) && (
              <Col xs="auto">
                <Button 
                  variant="danger" 
                  onClick={onLogoutSession}
                  className="logout-button"
                >
                  { 'Logout'}
                </Button>
              </Col>
            )}
          </Row>
        </Container>
      </header>

      <Container fluid className="py-4 app-shell">
        <Row>
          <Col>
            <div className="app-content-shell">
              <div className="p-4 app-panel">

                <div className="card mb-4 app-action-card">
                  <div className="card-body">
                    <h5 className="mb-2" style={{ color: 'var(--text-primary)', fontSize: '1.25rem', letterSpacing: '-0.3px', fontWeight: 700 }}>Factory Operations</h5>
                    <p className="mb-3" style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5', fontWeight: 400 }}>Simple factory workflow for BOM creation and factory file upload in your assigned factory</p>
                    <Row className="g-4 align-items-stretch">
                      <Col lg={6}>
                        <FileUpload 
                          onFileSelect={handleFileSelect} 
                          onUploadCSV={handleCSVUpload}
                          csvData={csvData}
                          bomData={bomData || {}}
                          supportedFactoryCodes={supportedFactoryCodes}
                          factoryBOMRows={factoryBOMRows}
                          isLoading={loading} 
                          resetTrigger={fileUploadResetTrigger}
                        />
                      </Col>
                      <Col lg={6}>
                        <Card className="mb-4 h-100 app-action-card">
                          <Card.Body className="app-action-card-body d-flex align-items-center justify-content-center">
                            <Button
                              className="bom-btn-primary"
                              size="lg"
                              onClick={() => {
                                setPreselectedCreateBOM12NC('');
                                setPreselectedCreateBOMFactoryCode('');
                                setCreateBOMDialogTrigger((currentValue) => currentValue + 1);
                              }}
                            >
                              Create Bill of material
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="card mb-4 app-action-card">
                  <div className="card-header app-action-card-header">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <h5 className="mb-0">Factory Bill Of Materials (BOM)</h5>
                      <div className="factory-bom-search-wrap">
                        <input
                          id="factory-bom-search-input"
                          type="search"
                          className="factory-bom-search-input"
                          placeholder="Search by 12NC or Effective Date"
                          value={factoryBOMSearchTerm}
                          onChange={(event) => setFactoryBOMSearchTerm(event.target.value)}
                          aria-label="Search Factory BOM table by 12NC or effective date"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {factoryBOMLoading && (
                      <div className="text-center py-3">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <span className="ms-2 text-muted">Loading factory BOM details...</span>
                      </div>
                    )}

                    {!factoryBOMLoading && (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover align-middle">
                          <thead>
                            <tr>
                              <th>
                                <div className="d-flex align-items-center gap-2">
                                  <span>LUMINAIRE ID (12NC)</span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    title={`Sort 12NC (${sort12NCOrder})`}
                                    onClick={() => setSort12NCOrder((current) => cycleSortOrder(current))}
                                  >
                                    {getSortIcon(sort12NCOrder)}
                                  </button>
                                </div>
                              </th>
                              <th>
                                <div className="d-flex align-items-center gap-2">
                                  <span>Factory Code</span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    title={`Cycle Factory Filter (current: ${factoryCodeFilter === 'all' ? 'All' : factoryCodeFilter})`}
                                    onClick={cycleFactoryCodeFilter}
                                  >
                                    {factoryCodeFilter === 'all' ? '🏭' : factoryCodeFilter}
                                  </button>
                                </div>
                              </th>
                              <th>Effective Date</th>
                              <th>
                                <div className="d-flex align-items-center gap-2">
                                  <span>Status</span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    title={`Cycle Uploaded Filter (current: ${uploadedFilter})`}
                                    onClick={cycleUploadedFilter}
                                  >
                                    {getUploadedFilterIcon()}
                                  </button>
                                </div>
                              </th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedFactoryRows.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-3">
                                  No factory BOM details found.
                                </td>
                              </tr>
                            ) : (
                              paginatedFactoryRows.map((row, index) => {
                                const hasBOMData = row.data !== null && row.data !== undefined;
                                return (
                                  <tr key={`${row.twelveNC}-${row.batchId}-${row.factoryCode}-${factoryTablePage}-${index}`}>
                                    <td>{row.twelveNC || '-'}</td>
                                    <td>{row.factoryCode || '-'}</td>
                                    <td>{row.effectiveDate || '-'}</td>
                                    <td>
                                      <span className={hasBOMData ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                        {hasBOMData ? '✓' : '✗'}
                                      </span>
                                    </td>
                                    <td>
                                      {hasBOMData ? (
                                        <button
                                          type="button"
                                          className="btn btn-link p-0"
                                          onClick={() => setViewBOMRow(row)}
                                        >
                                          View BOM
                                        </button>
                                      ) : (
                                        <a 
                                          href="#" 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setPreselectedCreateBOM12NC(String(row?.twelveNC || '').trim());
                                            setPreselectedCreateBOMFactoryCode(String(row?.factoryCode || '').trim());
                                            setCreateBOMDialogTrigger((currentValue) => currentValue + 1);
                                          }}
                                          style={{
                                            color: 'var(--primary-accent)',
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.color = 'var(--primary-dark)';
                                            e.target.style.textDecoration = 'underline';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.color = 'var(--primary-accent)';
                                            e.target.style.textDecoration = 'none';
                                          }}
                                        >
                                          Create Bill of material
                                        </a>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <small className="text-muted">
                            Showing {filteredAndSortedFactoryRows.length === 0 ? 0 : ((factoryTablePage - 1) * FACTORY_TABLE_PAGE_SIZE) + 1}
                            {' - '}
                            {Math.min(factoryTablePage * FACTORY_TABLE_PAGE_SIZE, filteredAndSortedFactoryRows.length)}
                            {' of '}
                            {filteredAndSortedFactoryRows.length}
                          </small>
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setFactoryTablePage((page) => Math.max(1, page - 1))}
                              disabled={factoryTablePage <= 1}
                            >
                              Previous
                            </Button>
                            <span className="text-muted small">
                              Page {factoryTablePage} / {totalFactoryPages}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setFactoryTablePage((page) => Math.min(totalFactoryPages, page + 1))}
                              disabled={factoryTablePage >= totalFactoryPages}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {loading && (
                  <div className="text-center my-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Processing CSV file...</p>
                  </div>
                )}

                <BOMDataDisplay
                  bomData={bomData || {}}
                  csvData={csvData}
                  onDataUpdate={handleDataUpdate}
                  onUploadSuccess={handleUploadSuccess}
                  onSubmit={handleSubmit}
                  isSubmitting={submitting}
                  showCreateBOMButton={false}
                  dialogOnly={true}
                  externalCreateBOMTrigger={createBOMDialogTrigger}
                  externalCreateBOM12NC={preselectedCreateBOM12NC}
                  externalCreateBOMFactoryCode={preselectedCreateBOMFactoryCode}
                  uploadedCSVFiles={uploadedCSVFiles}
                  supportedFactoryCodes={supportedFactoryCodes}
                  factoryBOMRows={factoryBOMRows}
                />

                <Modal show={viewBOMRow !== null} onHide={() => setViewBOMRow(null)} size="lg" centered>
                  <Modal.Header closeButton>
                    <Modal.Title>View BOM JSON</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className="mb-2">
                      <strong>12NC:</strong> {viewBOMRow?.twelveNC || '-'}
                    </div>
                    <div className="mb-3">
                      <strong>Factory Code:</strong> {viewBOMRow?.factoryCode || '-'}
                    </div>
                    <pre
                      style={{
                        maxHeight: '420px',
                        overflow: 'auto',
                        margin: 0,
                        backgroundColor: '#0f172a',
                        color: '#e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                      }}
                    >
                      {formatBOMPayload(viewBOMRow?.data)}
                    </pre>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={() => setViewBOMRow(null)}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

App.propTypes = {
  session: PropTypes.object,
  sessionLoading: PropTypes.bool,
  sessionError: PropTypes.string,
  bomData: PropTypes.object,
  csvData: PropTypes.object,
  loading: PropTypes.bool,
  submitting: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.string,
  onInitializeSession: PropTypes.func.isRequired,
  onLogoutSession: PropTypes.func.isRequired,
  onLoadBOMData: PropTypes.func.isRequired,
  onUpdateBOMData: PropTypes.func.isRequired,
  onSubmitBOMData: PropTypes.func.isRequired,
  onClearUploadedData: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  onClearSuccess: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  session: makeSelectSession(),
  sessionLoading: makeSelectSessionLoading(),
  sessionError: makeSelectSessionError(),
  bomData: makeSelectBOMData(),
  csvData: makeSelectCSVData(),
  loading: makeSelectLoading(),
  submitting: makeSelectSubmitting(),
  error: makeSelectError(),
  success: makeSelectSuccess(),
});

export function mapDispatchToProps(dispatch) {
  return {
    onInitializeSession: () => dispatch(initializeSession()),
    onLogoutSession: () => dispatch(logoutSessionRequest()),
    onLoadBOMData: (file) => dispatch(loadBOMData(file)),
    onUpdateBOMData: (data) => dispatch(updateBOMData(data)),
    onSubmitBOMData: (data) => dispatch(submitBOMData(data)),
    onClearUploadedData: (message) => dispatch(clearUploadedData(message)),
    onClearError: () => dispatch(clearError()),
    onClearSuccess: () => dispatch(clearSuccess()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(App);
